import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, UserPlus, FileSpreadsheet, Trash2 } from "lucide-react";

const STATUSES = ["nuovo", "contattato", "offerta", "vinto", "perso"];

const FIELDS = [
  { name: "company", label: "Azienda", required: true, full: true },
  { name: "contact_name", label: "Referente" },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Telefono" },
  { name: "city", label: "Città" },
  {
    name: "interest", label: "Interesse", type: "select",
    options: [
      { value: "vendita", label: "Vendita" },
      { value: "noleggio", label: "Noleggio" },
      { value: "manutenzione", label: "Manutenzione" },
    ],
  },
  { name: "value", label: "Valore stimato €", type: "number" },
  { name: "status", label: "Stato", type: "select", options: STATUSES.map((s) => ({ value: s, label: s })) },
  { name: "notes", label: "Note", type: "textarea", full: true },
];

export default function Lead() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/leads").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const save = async (values) => {
    try {
      if (editing) await api.put(`/leads/${editing.id}`, values);
      else await api.post("/leads", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const convert = async (id) => {
    await api.post(`/leads/${id}/convert`);
    load();
  };

  const quoteFor = async (lead) => {
    const { data } = await api.post("/quotes", {
      lead_id: lead.id,
      title: `Offerta ${lead.interest || "antincendio"} — ${lead.company}`,
    });
    navigate(`/preventivi?nuovo=${data.id}`);
  };

  return (
    <div data-testid="lead-page">
      <PageHeader title="Lead commerciali" subtitle={`${rows.length} opportunità`}>
        <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-lead-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo lead
        </Button>
      </PageHeader>

      <ListCard testid="lead-list">
        {rows.map((l) => (
          <div key={l.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{l.company}</p>
                <Badge className="rounded-sm capitalize" variant={l.status === "vinto" ? "default" : "secondary"}>
                  {l.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {l.contact_name || "—"} · {l.city || "—"} · {l.interest || "—"} · {eur(l.value)}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm"
                      onClick={() => { setEditing(l); setOpen(true); }} data-testid={`edit-lead-${l.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => convert(l.id)}
                      data-testid={`convert-lead-${l.id}`}>
                <UserPlus className="h-3.5 w-3.5 mr-2" /> Converti
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                      onClick={async () => { await api.delete(`/leads/${l.id}`); load(); }}
                      data-testid={`delete-lead-${l.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" className="rounded-sm" onClick={() => quoteFor(l)}
                      data-testid={`quote-lead-${l.id}`}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2" /> Preventivo
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun lead inserito." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica lead" : "Nuovo lead"}
                  fields={FIELDS} initial={editing || { status: "nuovo" }} onSubmit={save} testid="lead-dialog" />
    </div>
  );
}
